// src/components/child/StackedBarChart.jsx
import React, { useRef, useEffect, useState } from "react";
import * as Chart from "chart.js";
import HeadingComponent from "../BoardPack/components/shared/heading";

Chart.Chart.register(...Chart.registerables);

// Generic stacked bar chart — same datasets/months/yearRanges shape as LineBar,
// so it can render the same filtered Revenue Summary data as a stacked view.
const StackedBarChart = ({
  title,
  subtitle,
  datasets = [],
  yearRanges = [],
  months = [],
  yAxisTitle = "Revenue (LC)",
  topRightContent = null,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [themeKey, setThemeKey] = useState(0);
  // Chart.js reserves left/right margin width for the y-axis tick labels —
  // kept in sync with the chart's actual measured insets so the table's edge
  // columns stay pixel-aligned with the chart above them, same as LineBar.
  const [plotLeftInset, setPlotLeftInset] = useState(100);
  const [plotRightInset, setPlotRightInset] = useState(5);
  // One uniform font size for every data cell — sized to fit the single
  // widest value in the table, so every cell (biggest and smallest value
  // alike) renders at the same size.
  const [cellFontSize, setCellFontSize] = useState(10);

  useEffect(() => {
    const observer = new MutationObserver(() => setThemeKey((prev) => prev + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    const existingChart = Chart.Chart.getChart(chartRef.current);
    if (existingChart) existingChart.destroy();

    const ctx = chartRef.current.getContext("2d");
    const textColor = "#e5e7eb";

    const layoutSyncPlugin = {
      id: "layoutSync",
      afterLayout(chart) {
        const left = Math.round(chart.chartArea.left);
        const right = Math.round(chart.width - chart.chartArea.right);
        setPlotLeftInset((prev) => (prev === left ? prev : left));
        setPlotRightInset((prev) => (prev === right ? prev : right));
      },
    };

    chartInstanceRef.current = new Chart.Chart(ctx, {
      type: "bar",
      data: {
        labels: months,
        datasets: datasets.map((ds) => ({
          label: ds.label,
          data: ds.data.map((v) => (v == null ? 0 : v)),
          percentages: ds.percentages,
          backgroundColor: ds.color,
          borderColor: ds.color,
          borderWidth: 0,
          stack: "revenue",
          barPercentage: 0.65,
          categoryPercentage: 0.7,
        })),
      },
      options: {
        devicePixelRatio: (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1) * 2,
        responsive: false,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: textColor,
              font: { size: 10 },
              usePointStyle: true,
              pointStyle: "rect",
              padding: 15,
              boxWidth: 10,
              boxHeight: 10,
            },
          },
          tooltip: {
  mode: "index",
  intersect: false,
  backgroundColor: "rgba(30, 41, 59, 0.95)",
  titleColor: "#fff",
  bodyColor: "#fff",
  borderColor: "#475569",
  borderWidth: 1,
  padding: 8,
  callbacks: {
    // NAYA — month ke sath year bhi dikhao
    title: (items) => {
      const idx = items[0]?.dataIndex ?? 0;
      let count = 0;
      let year = '';
      for (const yr of yearRanges) {
        if (idx < count + yr.span) { year = yr.year; break; }
        count += yr.span;
      }
      return year ? `${months[idx]} ${year}` : months[idx];
    },
    label: (context) => {
      const val = context.parsed.y;
      const valStr = typeof val === "number" ? val.toLocaleString() : val;
      // API se aaya hua percentage seedha dikhao — client-side dobara calculate nahi karna.
      const pct = context.dataset.percentages?.[context.dataIndex];
      return typeof pct === "number" ? `${context.dataset.label}: ${valStr} (${pct}%)` : `${context.dataset.label}: ${valStr}`;
    },
  },
},
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            // Month + year ab niche table mein dikhte hain (LineBar jaisa), isliye
            // canvas par duplicate ticks nahi chahiye.
            ticks: { display: false },
            border: { display: false },
          },
          y: {
            stacked: true,
            grid: { display: false },
            ticks: {
              color: textColor,
              font: { size: 9 },
              callback: (v) => (typeof v === "number" ? v.toLocaleString() : v),
            },
            border: { display: false },
            // title: { display: true, text: yAxisTitle, color: textColor, font: { size: 10 } },
          },
        },
        layout: { padding: { left: 10, right: 10, top: 10, bottom: 10 } },
      },
      plugins: [layoutSyncPlugin],
    });

    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !chartInstanceRef.current) return;
      const w = containerRef.current.offsetWidth;
      const h = containerRef.current.offsetHeight;
      chartInstanceRef.current.resize(w, h);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [datasets, months, yearRanges, yAxisTitle, themeKey]);

  // Data-cell font size shrinks as more months are packed into the same
  // width so values stay inside their own column instead of spilling over —
  // same approach as LineBar.
  useEffect(() => {
    if (!cardRef.current || !months.length) return;

    const measureCanvas = document.createElement("canvas");
    const mctx = measureCanvas.getContext("2d");
    const BASE_SIZE = 10;
    const MIN_SIZE = 5;
    const CELL_PAD = 4;

    const recompute = () => {
      const totalWidth = cardRef.current?.offsetWidth ?? 0;
      const numCols = months.length;
      if (!totalWidth || !numCols) return;

      const perCol = Math.max(0, (totalWidth - plotLeftInset - plotRightInset) / numCols - CELL_PAD);

      mctx.font = `${BASE_SIZE}px Arial`;
      let widest = 0;
      datasets.forEach((ds) => {
        ds.data.forEach((val) => {
          const text = val == null ? "0" : typeof val === "number" ? val.toLocaleString() : String(val);
          widest = Math.max(widest, mctx.measureText(text).width);
        });
      });

      const size = widest > perCol && widest > 0
        ? Math.max(MIN_SIZE, Math.floor(BASE_SIZE * (perCol / widest)))
        : BASE_SIZE;
      setCellFontSize((prev) => (prev === size ? prev : size));
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, [datasets, months, plotLeftInset, plotRightInset]);

  const safeValue = (val) => {
    if (Array.isArray(val)) return "-";
    if (val == null) return "0";
    return typeof val === "number" ? val.toLocaleString() : val;
  };

  return (
    <div className="w-full">
      <div className="w-full mx-auto px-1 py-1 bg-card">
        <div className="text-center">
          {title && <HeadingComponent variant={"main"}>{title}</HeadingComponent>}
          {subtitle && <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>}
        </div>
        <div ref={cardRef} className="w-full p-0.5" style={{ backgroundColor: "#464647", borderRadius: "8px" }}>
          <div className="h-[550px] relative" ref={containerRef}>
            <canvas ref={chartRef}></canvas>
            {topRightContent && (
              <div className="absolute top-2 right-2 z-10">{topRightContent}</div>
            )}
          </div>

          <div className="w-full">
            <div className="overflow-x-auto">
              <table className="w-full !border-collapse text-xs table-fixed">
                <thead>
                  <tr>
                    <th className="py-0.5 px-1 text-center font-normal" style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px` }}></th>
                    {months.map((month, idx) => (
                      <th
                        key={idx}
                        className="!border !border-border px-0.5 py-0.5 text-center font-normal text-[10px] transition-colors hover:bg-white/15"
                        style={{ color: "#ffffff", borderColor: "#4b5563" }}
                      >
                        {month}
                      </th>
                    ))}
                    {plotRightInset > 0 && <th style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></th>}
                  </tr>
                  <tr>
                    <td className="py-0.5 px-1" style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px` }}></td>
                    {yearRanges.map((range, idx) => (
                      <td key={idx} colSpan={range.span} className="!border !border-border px-0.5 py-0.5 text-center text-[10px] font-bold" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                        {range.year}
                      </td>
                    ))}
                    {plotRightInset > 0 && <td style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></td>}
                  </tr>
                </thead>
                <tbody>
                  {datasets.map((dataset, dsIdx) => (
                    <tr key={dsIdx}>
                      <td
                        className="!border !border-border px-1 py-1.5 font-normal text-[8px] text-left whitespace-nowrap overflow-hidden text-ellipsis"
                        style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px`, color: "#ffffff", borderColor: "#4b5563" }}
                        title={dataset.label}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
                          <span style={{ display: "inline-block", width: 14, height: 8, backgroundColor: dataset.color, borderRadius: 1 }} />
                        </span>
                        {dataset.label}
                      </td>
                      {dataset.data.map((val, idx) => (
                        <td
                          key={idx}
                          className="!border !border-border px-0.5 py-1.5 text-center whitespace-nowrap overflow-hidden transition-colors hover:bg-white/15"
                          style={{ color: "#ffffff", borderColor: "#4b5563", fontSize: `${cellFontSize}px` }}
                          title={String(safeValue(val))}
                        >
                          {safeValue(val)}
                        </td>
                      ))}
                      {plotRightInset > 0 && <td style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-center py-0.5 mt-4 text-[10px] font-bold" style={{ color: "#ffffff" }}>
                Months
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StackedBarChart;
